/**
 * Le battement de présence — le cœur PUR (timers seuls, rien de React, rien
 * de réseau). `envoyer` décide QUOI dire au serveur ; ceci décide QUAND.
 *
 * Règles, et pourquoi :
 *   - un battement tout de suite, puis toutes les `intervalleMs` (30 s) ;
 *   - JAMAIS quand l'onglet n'est pas visible. Un chatteur qui a mis le
 *     cockpit derrière une autre fenêtre n'est pas devant ses discussions ;
 *     le serveur le laisse expirer (90 s = trois battements). Ça règle aussi
 *     le cas de la PWA en arrière-plan, où le navigateur ralentit les timers :
 *     on ne compte pas sur eux, on ne bat pas ;
 *   - un battement immédiat au retour en onglet visible, pour ne pas attendre
 *     le prochain tick ;
 *   - un envoi raté est retenté UNE fois après 2 à 5 s (un trou réseau ne
 *     doit pas coûter un tick entier), jamais en chaîne : si la reprise rate
 *     aussi, la cadence normale reprend la main.
 *
 * `doc` est injectable pour les tests.
 */

/** Cadence du battement. Le serveur (`REGIE_PRESENCE_STALE_SECONDS`, 90 s)
 *  tolère deux battements perdus avant de faire disparaître quelqu'un. */
export const PRESENCE_BATTEMENT_MS = 30_000;

export type DocumentVisible = Pick<Document, "addEventListener" | "removeEventListener"> & {
  readonly visibilityState: DocumentVisibilityState;
};

export interface Battement {
  /** Coupe tout — aucun battement après. */
  arreter(): void;
  /** Un battement hors cadence (ex. : le chatteur vient de se mettre en pause,
   *  le serveur doit le savoir maintenant, pas dans 30 s). */
  maintenant(): void;
}

/** Délai de la reprise après un envoi raté : [2 s, 5 s). `rand` injectable. */
export function delaiDeReprise(rand: () => number = Math.random): number {
  return 2_000 + rand() * 3_000;
}

export function battreLaPresence(
  envoyer: () => Promise<unknown>,
  intervalleMs: number,
  doc: DocumentVisible = document,
  reprise: () => number = delaiDeReprise,
): Battement {
  let repriseTimer: ReturnType<typeof setTimeout> | null = null;

  function annulerLaReprise() {
    if (repriseTimer) {
      clearTimeout(repriseTimer);
      repriseTimer = null;
    }
  }

  function battre(estUneReprise = false) {
    annulerLaReprise();
    if (doc.visibilityState !== "visible") return;
    envoyer().catch(() => {
      if (estUneReprise) return;
      repriseTimer = setTimeout(() => battre(true), reprise());
    });
  }

  battre();
  const intervalle = setInterval(() => battre(), intervalleMs);
  const surVisibilite = () => {
    if (doc.visibilityState === "visible") battre();
  };
  doc.addEventListener("visibilitychange", surVisibilite);

  return {
    arreter() {
      clearInterval(intervalle);
      annulerLaReprise();
      doc.removeEventListener("visibilitychange", surVisibilite);
    },
    maintenant() {
      battre();
    },
  };
}
